import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { ReportMetricCard } from "@/features/reports/components/ReportMetricCard";
import { ReportSectionError } from "@/features/reports/components/ReportSectionError";
import { ReportTableSkeleton } from "@/features/reports/components/ReportTableSkeleton";
import { ExportFormatMenu } from "@/features/content/components/ExportFormatMenu";
import {
  isReportSectionLoading,
  normalizeReportDateFilters,
} from "@/features/reports/lib/report-query-utils";
import { reportService } from "@/features/reports/services/reportService";
import { handleApiError } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type { ContentExportFormat } from "@/types/content";

interface ContentReportPanelProps {
  from: string;
  to: string;
  onChangeFrom: (value?: string) => void;
  onChangeTo: (value?: string) => void;
  onClear: () => void;
}

/**
 * Content report (§5.12) — published vs unpublished by department, with a date-range
 * filter and CSV/Excel export. Shared by the Content Oversight page's "reports" tab
 * (/admin/pages?tab=reports) and the Reporting & Analytics page (/admin/analytics).
 */
export function ContentReportPanel({
  from,
  to,
  onChangeFrom,
  onChangeTo,
  onClear,
}: ContentReportPanelProps) {
  const [isExporting, setIsExporting] = useState(false);

  const reportQuery = useMemo(
    () => normalizeReportDateFilters(from, to),
    [from, to],
  );

  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.reports.content(reportQuery),
    queryFn: () => reportService.contentReport(reportQuery),
  });

  const isLoading = isReportSectionLoading(isPending, isFetching, data);

  const exportReport = async (format: ContentExportFormat) => {
    setIsExporting(true);
    try {
      await reportService.exportContentReport({ ...reportQuery, format });
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تصدير التقرير" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          من تاريخ
          <input
            type="date"
            value={from}
            onChange={(e) => onChangeFrom(e.target.value || undefined)}
            className="rounded-md border border-border bg-transparent px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          إلى تاريخ
          <input
            type="date"
            value={to}
            onChange={(e) => onChangeTo(e.target.value || undefined)}
            className="rounded-md border border-border bg-transparent px-2 py-1 text-sm"
          />
        </label>
        {(from || to) && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            مسح
          </Button>
        )}
        <ExportFormatMenu
          isExporting={isExporting}
          onExport={exportReport}
          label="تصدير"
        />
      </div>

      {error ? (
        <ReportSectionError onRetry={() => refetch()} />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <ReportMetricCard
              title="صفحات عادية"
              value={data?.pages ?? "—"}
              isLoading={isLoading}
            />
            <ReportMetricCard
              title="أخبار"
              value={data?.news ?? "—"}
              isLoading={isLoading}
            />
            <ReportMetricCard
              title="منشور"
              value={data?.totalPublished ?? "—"}
              valueClassName="text-primary"
              isLoading={isLoading}
            />
            <ReportMetricCard
              title="غير منشور"
              value={data?.totalUnpublished ?? "—"}
              valueClassName="text-muted-foreground"
              isLoading={isLoading}
            />
          </div>

          {isLoading ? (
            <ReportTableSkeleton
              title="المنشور مقابل غير المنشور حسب الدائرة"
              columns={4}
            />
          ) : (
            <div className="rounded-xl border border-border bg-card text-sm shadow-sm">
              <div className="border-b border-border px-3 py-2 text-right text-xs font-medium text-foreground">
                المنشور مقابل غير المنشور حسب الدائرة
              </div>
              <table className="min-w-full divide-y divide-border text-xs">
                <thead className="bg-muted/30 text-[11px] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-right">الدائرة</th>
                    <th className="px-3 py-2 text-center">الإجمالي</th>
                    <th className="px-3 py-2 text-center">منشور</th>
                    <th className="px-3 py-2 text-center">غير منشور</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(data?.byDepartment ?? []).map((row) => (
                    <tr
                      key={row.departmentId ?? "unassigned"}
                      className="hover:bg-muted/20"
                    >
                      <td className="px-3 py-1.5 text-right">
                        {row.nameAr ?? row.name ?? "غير مصنّفة"}
                      </td>
                      <td className="px-3 py-1.5 text-center">{row.total}</td>
                      <td className="px-3 py-1.5 text-center text-primary">
                        {row.published}
                      </td>
                      <td className="px-3 py-1.5 text-center text-muted-foreground">
                        {row.unpublished}
                      </td>
                    </tr>
                  ))}
                  {(data?.byDepartment?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={4} className="p-0">
                        <EmptyState icon={FileText} title="لا توجد بيانات" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
